import React from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveMediaUrl } from '../../utils/api';
import { Coffee, ArrowLeft, User, ShoppingBag, X, Menu, MessageCircle } from 'lucide-react';

export interface CustomerHeaderProps {
  showBack?: boolean;
  onBack?: () => void;
  showProfile?: boolean;
  isProfileActive?: boolean;
  profileName?: string;
  onOpenProfile?: () => void;
  showCart?: boolean;
  cartCount?: number;
  onOpenCart?: () => void;
  showClose?: boolean;
  onClose?: () => void;
  showWhatsApp?: boolean;
  whatsappLink?: string;
  showMenuToggle?: boolean;
  isMenuOpen?: boolean;
  onToggleMenu?: () => void;
  onBrandClick?: () => void;
  showLocationPill?: boolean;
  children?: React.ReactNode;
}

export const CustomerHeader: React.FC<CustomerHeaderProps> = ({
  showBack,
  onBack,
  showProfile,
  isProfileActive,
  profileName,
  onOpenProfile,
  showCart,
  cartCount = 0,
  onOpenCart,
  showClose,
  onClose,
  showWhatsApp,
  whatsappLink,
  showMenuToggle,
  isMenuOpen,
  onToggleMenu,
  onBrandClick,
  showLocationPill,
  children,
}) => {
  const { data } = useContent();
  const siteSettings = data?.siteSettings || { brandName: 'LETONKU', logoUrl: '' };

  return (
    <header
      className="sticky top-0 left-0 right-0 z-40 shrink-0 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0] transition-all duration-200"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="h-14 sm:h-16 max-w-4xl mx-auto px-4 flex items-center justify-between gap-3">
        {/* Left: Optional Back Button + Logo & Master Brand */}
        <div className="flex items-center gap-3 shrink-0">
          {showBack && onBack && (
            <button
              onClick={onBack}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] hover:border-[#0056D2]/40 transition-all cursor-pointer shrink-0"
              title="Kembali"
              aria-label="Kembali"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}

          <div
            onClick={onBrandClick}
            className={`flex items-center gap-2.5 group ${onBrandClick ? 'cursor-pointer' : ''}`}
          >
            <div className="relative w-9 h-9 rounded-xl overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC] shrink-0 flex items-center justify-center">
              {siteSettings.logoUrl ? (
                <img
                  src={resolveMediaUrl(siteSettings.logoUrl)}
                  alt={siteSettings.brandName || 'LetonKu'}
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-full h-full bg-[#0A192F] text-white flex items-center justify-center font-black">
                  <Coffee className="w-4 h-4" />
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center text-left">
              <span className="font-bold text-sm sm:text-base tracking-tight text-[#0A192F] leading-snug">
                {siteSettings.brandName || 'LetonKu'}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider leading-none mt-0.5">
                Online Ordering
              </span>
            </div>
          </div>

          {showLocationPill && (
            <div className="hidden xl:flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F8FAFC] text-[#0A192F] border border-[#E2E8F0] text-xs font-semibold ml-2">
              <span className="w-2 h-2 rounded-full bg-[#0056D2] animate-pulse" />
              <span>Open Daily 08:00 - 23:00</span>
            </div>
          )}
        </div>

        {/* Center Children */}
        {children && <div className="hidden lg:flex items-center gap-1">{children}</div>}

        {/* Right Action CTAs */}
        <div className="flex items-center gap-2 shrink-0">
          {showWhatsApp && whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-[#F8FAFC] hover:bg-slate-100 border border-[#E2E8F0] text-[#0A192F] transition-all"
            >
              <MessageCircle className="w-3.5 h-3.5 text-[#0056D2]" />
              <span>WA</span>
            </a>
          )}

          {showProfile && onOpenProfile && (
            <button
              onClick={onOpenProfile}
              className={`p-2 sm:px-3.5 sm:py-2 rounded-xl border text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                isProfileActive
                  ? 'bg-[#0056D2] text-white border-[#0056D2]'
                  : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#0A192F] hover:border-[#0056D2]/40'
              }`}
              title={profileName ? `Member: ${profileName}` : 'Login / Member'}
            >
              <User className="w-4 h-4 text-current" />
              <span className="hidden sm:inline font-semibold">
                {profileName ? profileName.trim().split(' ')[0] : 'Akun'}
              </span>
            </button>
          )}

          {showCart && onOpenCart && (
            <button
              onClick={onOpenCart}
              className="relative w-10 h-10 sm:w-auto sm:px-3.5 sm:py-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] hover:border-[#0056D2]/40 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer"
              title="Shopping Cart"
            >
              <ShoppingBag className="w-4 h-4 text-[#0056D2]" />
              <span className="hidden sm:inline">Cart</span>
              {cartCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-[#0056D2] text-white text-[10px] font-bold">
                  {cartCount}
                </span>
              )}
            </button>
          )}

          {showClose && onClose && (
            <button
              onClick={onClose}
              className="w-10 h-10 sm:w-auto sm:px-3 sm:py-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-slate-500 hover:text-[#0A192F] transition-all cursor-pointer flex items-center justify-center gap-1.5 text-xs"
              aria-label="Tutup"
              title="Tutup"
            >
              <span className="hidden sm:inline text-[11px] font-bold">Close</span>
              <X className="w-4 h-4" />
            </button>
          )}

          {showMenuToggle && onToggleMenu && (
            <button
              onClick={onToggleMenu}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] lg:hidden focus:outline-none cursor-pointer"
              aria-label="Toggle Navigation Menu"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
